import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { setKioskToken } from '@/hooks/useKiosk';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { TEST_MODE_KEY } from '@/components/BarcodeScanner';

const TEST_MODE_TAP_THRESHOLD = 5;
const TEST_MODE_TAP_WINDOW_MS = 4000;

/**
 * Kiosk-Setup view. Persists the operator-supplied token under
 * `localStorage.kiosk-token` and navigates to /kiosk on success.
 *
 * Tapping the PA logo 5 times within 4 s flips
 * `localStorage.scanner-test-mode` to "true" and surfaces a toast — the
 * scanner test-mode (CLAUDE.md §22.3) reads this flag on next mount.
 */
export function KioskSetupPage(): JSX.Element {
  useDocumentTitle('Kiosk-Setup · PA-Tracker');
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const tapStateRef = useRef<{ count: number; firstAt: number }>({
    count: 0,
    firstAt: 0,
  });

  function activate(): void {
    const trimmed = token.trim();
    if (!trimmed) {
      setError('Bitte einen Token eingeben.');
      return;
    }
    setError(null);
    setKioskToken(trimmed);
    navigate('/', { replace: true });
  }

  function handleLogoTap(): void {
    const now = Date.now();
    const state = tapStateRef.current;
    if (now - state.firstAt > TEST_MODE_TAP_WINDOW_MS) {
      tapStateRef.current = { count: 1, firstAt: now };
      return;
    }
    state.count += 1;
    if (state.count >= TEST_MODE_TAP_THRESHOLD) {
      window.localStorage.setItem(TEST_MODE_KEY, 'true');
      tapStateRef.current = { count: 0, firstAt: 0 };
      toast.success('Test-Modus aktiviert. Beide Scanner laufen parallel.');
    }
  }

  return (
    <main className="container max-w-lg py-12 [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]">
      <button
        type="button"
        onClick={handleLogoTap}
        className="mb-6 inline-flex items-center gap-3 rounded-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label="ELSA-Logo (5x tippen für Test-Modus)"
        data-testid="pa-logo-tap"
      >
        <span
          aria-hidden
          className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="h-6 w-6"
            aria-hidden
          >
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
            <path d="M7 12h10" />
          </svg>
        </span>
        <span className="text-left">
          <span className="block text-lg font-semibold leading-tight">Projektarbeit</span>
          <span className="block text-xs text-muted-foreground">Kiosk-Einrichtung</span>
        </span>
      </button>

      <h1 className="text-3xl font-semibold">Kiosk aktivieren</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Token aus dem Admin-Bereich eingeben. Nach Aktivierung läuft das iPad im Kiosk-Modus.
      </p>

      <form
        className="mt-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          activate();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="kiosk-token">Kiosk-Token</Label>
          <Input
            id="kiosk-token"
            type="text"
            inputMode="text"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="kiosk-token-input"
          />
          {error ? (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          ) : null}
        </div>
        <Button type="submit" size="lg" className="w-full text-base" data-testid="kiosk-activate">
          Kiosk aktivieren
        </Button>
      </form>
    </main>
  );
}
